import React, { useState, useEffect } from 'react';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { TrainingState, useTrainingStore } from '~/training/state';
import styled from 'styled-components';
import UserIcon from '~/assets/icons/user.svg';
import ChevronIcon from '~/assets/icons/chevron.svg';
import EmailIcon from '~/assets/icons/email.svg';
import PhoneIcon from '~/assets/icons/phone.svg';

const selector = (s: TrainingState) => select(s, 'companyContacts');

const Chevron = styled(ChevronIcon)`
  margin-left: 8px;
  stroke: var(--color-black);
`;

const PointOfContactContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;

  h3 {
    color: dark;
    font-family: ralewayBold;
    font-size: 14px;
    font-weight: 700;
    line-height: normal;
    letter-spacing: 1.4px;
    text-transform: uppercase;
  }
`;

const CollapsSectionContainer = styled.div`
  border-radius: var(--8, 8px);
  border: 1px solid var(--Grey-3, #e0e0e0);
  background: var(--Grey-1, #fff);
  padding: 4px 20px;
  cursor: pointer;

  .collaps-section-header {
    display: flex;
    flex-direction: row;
    cursor: pointer;

    .chevron {
      margin-left: auto;
      margin-top: 20px;

      svg {
        transform: rotate(90deg);
      }

      .open {
        transform: rotate(270deg);
      }
    }

    .name {
      /* max-width: 180px; */
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;

      .name-text {
        padding-top: 4px;
        color: var(--Grey-6, #2e2e2e);
        font-size: 18px;
        font-weight: 700;
        line-height: 150%;
        font-family: ralewayBold;
      }
      .department {
        color: var(--Grey-5, #6f6f6f);
        font-family: Raleway;
        font-size: 12px;
        font-weight: 400;
        line-height: 150%;
      }
    }
  }

  .collaps-section-content {
    display: flex;
    flex-direction: column;
    gap: 10px;

    .other-info {
      display: flex;
      flex-direction: column;

      b {
        color: var(--Grey-6, #2e2e2e) !important;
        font-family: ralewayBold;
        font-size: 12px;
        font-weight: 700;
        line-height: 150%;
        text-transform: uppercase;
      }

      div {
        color: var(--Grey-5, #6f6f6f);
        font-size: 14px;
        font-weight: 400;
        line-height: 150%;
      }
    }

    .email {
      padding-top: 10px;
      color: var(--Purple-4, #9738ff);
      font-family: Raleway;
      font-size: 14px;
      font-weight: 400;
      line-height: 150%;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;

      a {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
    .phone {
      color: var(--Purple-4, #9738ff);
      font-family: Raleway;
      font-size: 14px;
      font-weight: 400;
      line-height: 150%;
      text-decoration-line: underline;
      text-decoration-style: solid;
      text-decoration-skip-ink: none;
      text-decoration-thickness: auto;
      text-underline-offset: auto;
      text-underline-position: from-font;

      a {
        display: flex;
        align-items: center;
        gap: 8px;
      }
    }
    .other-info {
      color: var(--Grey-5, #6f6f6f);
    }
  }

  .icon {
    margin-top: 5px;
    margin-right: 12px;
    display: flex;
    width: 48px;
    height: 48px;
    padding: 5.85px 6.825px;
    justify-content: center;
    align-items: center;
    flex-shrink: 0;
    background: var(--Grey-2, #f5f5f5);
    border-radius: 50%;
  }
`;

const CollapsSection = ({
  data,
  index,
  openIndex,
  setOpenIndex
}: {
  data: {
    name?: string;
    department?: string;
    email?: string;
    phone?: string;
    otherInfo?: string;
  };
  index: number;
  openIndex: number | null;
  setOpenIndex: (index: number | null) => void;
}) => {
  const onClickEvent = () => {
    if (openIndex == index) {
      setOpenIndex(null);
    } else {
      setOpenIndex(index);
    }
  };

  const onKeyDownEvent = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClickEvent();
    }
  };

  return (
    <CollapsSectionContainer>
      <div
        className="collaps-section-header"
        onClick={onClickEvent}
        onKeyDown={onKeyDownEvent}
        role="button"
        tabIndex={0}
      >
        <div className="icon">
          <UserIcon />
        </div>
        <div className="name">
          <div className="name-text">{data.name}</div>
          <div className="department">{data.department}</div>
        </div>
        <div className="chevron">
          <Chevron className={openIndex == index ? 'open' : ''} />
        </div>
      </div>
      <div className="collaps-section-content">
        {openIndex == index && data.email && (
          <div className="email">
            <a href={`mailto:${data.email}`}>
              <EmailIcon />
              {data.email}
            </a>
          </div>
        )}
        {openIndex == index && data.phone && (
          <div className="phone">
            <a href={`tel:${data.phone}`}>
              <PhoneIcon />
              {data.phone}
            </a>
          </div>
        )}
        {openIndex == index && data.otherInfo && (
          <div className="other-info">
            <b>{t`Other Details`}</b>
            <div>{data.otherInfo}</div>
          </div>
        )}
      </div>
    </CollapsSectionContainer>
  );
};

const PointOfContact = ({
  title = t`Your point of contact`
}: {
  title?: string;
}) => {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const { companyContacts } = useTrainingStore(selector);

  if (!companyContacts || companyContacts.length === 0) return null;

  return (
    <PointOfContactContainer>
      <h3>{title}</h3>
      {companyContacts.map((contact, index) => (
        <CollapsSection
          key={index}
          data={contact}
          openIndex={openIndex}
          setOpenIndex={setOpenIndex}
          index={index}
        />
      ))}
    </PointOfContactContainer>
  );
};

export default PointOfContact;
